import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Bell, Check, BellOff, BellRing, Send } from 'lucide-react';
import { usePushSubscription } from '../lib/usePushSubscription';
import { formatActivityDay } from '../lib/format';

const POLL_INTERVAL_MS = 30_000;

const relTime = (iso) => {
  return formatActivityDay(iso);
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState('');
  const wrapRef = useRef(null);
  const push = usePushSubscription();

  const load = async () => {
    try {
      const r = await api.get('/notifications', { params: { limit: 20 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (n) => {
    if (n.is_read) return;
    try { await api.post(`/notifications/${n.id}/read`); }
    catch (err) { console.warn('Mark-as-read failed', err); }
    setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/read-all'); }
    catch (err) { console.warn('Mark-all-read failed', err); }
    setItems((cur) => cur.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="notification-bell-wrap">
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn btn-outline btn-sm relative"
        title="Notifications"
        data-testid="notification-bell-btn"
        aria-label={`Notifications (${unread} unread)`}
      >
        <Bell size={14}/>
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 rounded-full text-[10px] font-bold flex items-center justify-center"
            style={{ background: '#DC2626', color: 'white', minWidth: '16px', height: '16px', padding: '0 4px' }}
            data-testid="notification-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
        <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setOpen(false)} />
        <div
          className="fixed top-14 left-2 right-2 sm:absolute sm:top-auto sm:left-auto sm:right-0 sm:mt-2 sm:w-80 rounded-lg shadow-2xl sm:shadow-lg z-50 overflow-hidden"
          style={{ background: 'white', border: '1px solid var(--cc-border)' }}
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--cc-border)', background: 'var(--cc-surface)' }}>
            <div className="font-head text-sm font-bold" style={{ color: 'var(--cc-dark-green)' }}>Notifications</div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs hover:underline flex items-center gap-1" style={{ color: 'var(--cc-accent)' }} data-testid="notification-mark-all-read">
                <Check size={11}/> Mark all read
              </button>
            )}
          </div>

          {/* Web Push toggle — out-of-tab phone-style alerts */}
          {/* push.supported && (
            <div className="px-3 py-2 border-b text-xs flex items-center gap-2" style={{ borderColor: 'var(--cc-border)' }} data-testid="push-toggle-row">
              {push.subscribed ? (
                <BellRing size={13} style={{ color: '#10B981' }} />
              ) : (
                <BellOff size={13} style={{ color: 'var(--cc-text-muted)' }} />
              )}
              <span className="flex-1" style={{ color: 'var(--cc-text-muted)' }}>
                {push.subscribed ? 'Phone alerts ON for this device' : 'Get alerts even when this tab is closed'}
              </span>
              {push.subscribed ? (
                <>
                  <button
                    onClick={async () => {
                      setTesting(true);
                      try { const r = await push.sendTest(); setToast(`Test sent (${r.sent}/${r.total})`); }
                      catch { setToast('Test failed'); }
                      finally { setTesting(false); setTimeout(() => setToast(''), 3000); }
                    }}
                    disabled={testing}
                    className="px-2 py-0.5 rounded text-[11px] font-semibold hover:underline disabled:opacity-50"
                    style={{ color: 'var(--cc-accent)' }}
                    title="Send a test notification"
                    data-testid="push-test-btn"
                  >
                    <Send size={11} className="inline mr-0.5"/>Test
                  </button>
                  <button
                    onClick={push.unsubscribe}
                    disabled={push.busy}
                    className="px-2 py-0.5 rounded text-[11px] font-semibold hover:underline disabled:opacity-50"
                    style={{ color: '#B91C1C' }}
                    data-testid="push-disable-btn"
                  >
                    Disable
                  </button>
                </>
              ) : (
                <button
                  onClick={push.subscribe}
                  disabled={push.busy}
                  className="px-2 py-0.5 rounded text-[11px] font-semibold"
                  style={{ background: 'var(--cc-dark-green)', color: 'white' }}
                  data-testid="push-enable-btn"
                >
                  Enable
                </button>
              )}
            </div>
          ) */}
          {/* push.error && (
            <div className="px-3 py-1.5 text-[11px]" style={{ background: '#FEE2E2', color: '#991B1B' }} data-testid="push-error">{push.error}</div>
          ) */}
          {toast && (
            <div className="px-3 py-1.5 text-[11px]" style={{ background: '#D1FAE5', color: '#065F46' }} data-testid="push-toast">{toast}</div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs italic" style={{ color: 'var(--cc-text-muted)' }} data-testid="notification-empty">
                You're all caught up.
              </div>
            ) : (
              items.map((n) => (
                <Link
                  key={n.id}
                  to={n.type === 'payment_reminder' ? '/accounting-tasks' : n.type === 'task' ? '/tasks' : (n.related_visit_id ? `/site-visits/${n.related_visit_id}` : '/site-visits')}
                  onClick={() => { markRead(n); setOpen(false); }}
                  className="block px-3 py-2.5 border-b text-sm hover:bg-emerald-50/40"
                  style={{ borderColor: 'var(--cc-border)', background: n.is_read ? 'white' : 'rgba(16,185,129,0.06)' }}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && (
                      <span className="mt-1.5 inline-block rounded-full" style={{ width: '7px', height: '7px', background: '#10B981' }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm leading-tight ${n.is_read ? '' : 'font-semibold'}`}>{n.message}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: 'var(--cc-text-muted)' }}>{relTime(n.created_at)}</div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
